/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Mysov Sergey
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.15.0
 */

import { Fragment, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import ButtonServiceUserDenied from '@/main/modules/role/components/ButtonServiceUserDenied';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';

import { icons } from '../../../../assets';
import { LinkBlock } from '../../../../components/common';
import { ImgStyled } from '../../../../components/common/Block/Img';
import EllipsisText from '../../../../components/common/EllipsisText/EllipsisText';
import { ELinkTarget } from '../../../../components/common/ExternalLink/ExternalLink';
import { API_ORDER_ID_FILE_DOWNLOAD } from '../../../../modules/app/Api';
import { EFileExtension, MapExtensionToIcon } from '../../../../modules/app/appConstants';
import { makeSelectPageOrderIdTranslation } from '../../../../modules/internationalization/intSelectors';
import { modalOpen } from '../../../../modules/modal/modalActions';
import { getFileExtension } from '../../../../utils/formatText';
import { stringFormatParams } from '../../../../utils/stringFormat';
import { IOrderIdFile } from '../../types';
import { OrderIdFileStyled } from '../styled/OrderIdFileStyled';
import OrderIdFileSendModal from './OrderIdFileSendModal';

export interface IOrderIdFileProps {
    file: IOrderIdFile;
    isCdmAdditionalReportsEnabled: boolean;
    orderId: string;
}

const OrderIdFileList = ({ isCdmAdditionalReportsEnabled, orderId, file }: IOrderIdFileProps) => {
    const dispatch = useDispatch();

    const translationOrderId = useSelector(makeSelectPageOrderIdTranslation());

    const openFileModal = useCallback(() => {
        dispatch(
            modalOpen({
                render: (p) => (
                    <OrderIdFileSendModal
                        defaultEmail={file.email}
                        isCdmReport={file.isCdmReport}
                        orderId={orderId}
                        onCloseModal={p.onCloseModal}
                    />
                ),
            }),
        );
    }, [file, orderId, dispatch]);

    const getFileDownloadUrl = useCallback(
        (fileId: number) => stringFormatParams(API_ORDER_ID_FILE_DOWNLOAD, { orderId, fileId }),
        [orderId],
    );

    return (
        <Fragment>
            <div className="order__block">
                {file.files.map((f) => {
                    const fileExtension = getFileExtension(f.fileName) as EFileExtension;
                    const fileIcon = MapExtensionToIcon[fileExtension] || icons.defaultFileIcon;
                    return (
                        <OrderIdFileStyled>
                            <ImgStyled className="order-file__icon medium" src={fileIcon} />
                            <EllipsisText lines={2} title={f.fileName}>
                                <LinkBlock isExternalUrl target={ELinkTarget.BLANK} url={getFileDownloadUrl(f.fileId)}>
                                    {f.fileName}
                                </LinkBlock>
                            </EllipsisText>
                        </OrderIdFileStyled>
                    );
                })}
            </div>
            <ArrowTooltip disableHoverListener={!isCdmAdditionalReportsEnabled || file.isCdmReport}
                          title={translationOrderId.file.reportNotAvailable}>
                <span>
                    <ButtonServiceUserDenied dataName="order-id-open-modal-btn"
                                             disabled={isCdmAdditionalReportsEnabled && !file.isCdmReport}
                                             onClick={openFileModal}>
                        {translationOrderId.file.btn}
                    </ButtonServiceUserDenied>
                </span>
            </ArrowTooltip>
        </Fragment>
    );
};

export default OrderIdFileList;
